/**
 * Tokens semánticos de estados de negocio (atenciones, autorizaciones, prestadores, etc.).
 * Valores default; Experience puede sobreescribirlos vía AppThemeService.
 */
export type StatusThemeTokens = {
    statusScheduled: string;
    statusScheduledBg: string;
    statusDone: string;
    statusDoneBg: string;
    statusInProgress: string;
    statusInProgressBg: string;
    statusNotDone: string;
    statusNotDoneBg: string;
    statusInformed: string;
    statusInformedBg: string;
    statusBillable: string;
    statusBillableBg: string;
    statusInProcess: string;
    statusDebitedBg: string;
    statusAuthorized: string;
    statusAuthorizedBg: string;
    statusExpiringSoon: string;
    statusExpiringSoonBg: string;
    statusExpired: string;
    statusExpiredBg: string;
    statusUnauthorized: string;
    statusUnauthorizedBg: string;
    statusProviderActive: string;
    statusProviderInactive: string;
    statusProviderPossible: string;
    statusProviderCancelled: string;
    statusProviderActiveBg: string;
    statusProviderInactiveBg: string;
    statusProviderPossibleBg: string;
    statusProviderCancelledBg: string;
    statusSuccess: string;
    statusSuccessBg: string;
    statusWarning: string;
    statusWarningBg: string;
    statusInfo: string;
    statusInfoBg: string;
    statusNeutral: string;
    statusNeutralBg: string;
    statusDisabled: string;
    statusDisabledBg: string;
    mapPatientActive: string;
    mapPatientInactive: string;
    mapProviderActive: string;
    mapProviderInactive: string;
    mapMarkerProviderActive: string;
    mapMarkerProviderInactive: string;
    mapMarkerProviderPossible: string;
    mapMarkerProviderCancelled: string;
    mapMarkerPatientActive: string;
    mapMarkerPatientInactive: string;
    mapMarkerCluster: string;
    white: string;
    black: string;
    grey: string;
    greyLight: string;
    greyMedium: string;
    greyDark: string;
    divider: string;
    borderLight: string;
    shadow: string;
};

export const DEFAULT_STATUS_THEME: StatusThemeTokens = {
    statusScheduled: '#609e50',
    statusScheduledBg: '#eafee5',
    statusDone: '#093bbb',
    statusDoneBg: '#d6def4',
    statusInProgress: '#ff7a00',
    statusInProgressBg: '#ffebd9',
    statusNotDone: '#d02700',
    statusNotDoneBg: '#ffe9e9',
    statusInformed: '#1994b1',
    statusInformedBg: '#d9f1f6',
    statusBillable: '#37474f',
    statusBillableBg: '#a1dfcc',
    statusInProcess: '#d7b1f2',
    statusDebitedBg: '#c6ccce',
    statusAuthorized: '#609e50',
    statusAuthorizedBg: '#eafee5',
    statusExpiringSoon: '#ea750b',
    statusExpiringSoonBg: '#fff4eb',
    statusExpired: '#d02700',
    statusExpiredBg: '#ffe9e9',
    statusUnauthorized: '#7a0060',
    statusUnauthorizedBg: '#ffd9f7',
    statusProviderActive: '#37474f',
    statusProviderInactive: '#37474f',
    statusProviderPossible: '#2668cb',
    statusProviderCancelled: '#ffffff',
    statusProviderActiveBg: '#a1dfcc',
    statusProviderInactiveBg: '#c6ccce',
    statusProviderPossibleBg: '#e0ecff',
    statusProviderCancelledBg: '#d02700',
    statusSuccess: '#609e50',
    statusSuccessBg: '#eafee5',
    statusWarning: '#ffb800',
    statusWarningBg: '#fff2df',
    statusInfo: '#2a8ec7',
    statusInfoBg: '#e0f4ff',
    statusNeutral: '#9e9e9e',
    statusNeutralBg: '#ecebeb',
    statusDisabled: '#d3d3d3',
    statusDisabledBg: '#f5f5f5',
    mapPatientActive: '#609e50',
    mapPatientInactive: '#9e9e9e',
    mapProviderActive: '#441d99',
    mapProviderInactive: '#9e9e9e',
    mapMarkerProviderActive: '#008919',
    mapMarkerProviderInactive: '#939393',
    mapMarkerProviderPossible: '#ff9c2b',
    mapMarkerProviderCancelled: '#d02700',
    mapMarkerPatientActive: '#008919',
    mapMarkerPatientInactive: '#939393',
    mapMarkerCluster: '#441d99',
    white: '#ffffff',
    black: '#000000',
    grey: '#3b3b3b',
    greyLight: '#e0e0e0',
    greyMedium: '#9e9e9e',
    greyDark: '#616161',
    divider: '#ecebeb',
    borderLight: '#e0e0e0',
    shadow: '#bcbcbc',
};

export const STATUS_THEME_CSS_VARS: Record<keyof StatusThemeTokens, string> = {
    statusScheduled: '--status-scheduled',
    statusScheduledBg: '--status-scheduled-bg',
    statusDone: '--status-done',
    statusDoneBg: '--status-done-bg',
    statusInProgress: '--status-in-progress',
    statusInProgressBg: '--status-in-progress-bg',
    statusNotDone: '--status-not-done',
    statusNotDoneBg: '--status-not-done-bg',
    statusInformed: '--status-informed',
    statusInformedBg: '--status-informed-bg',
    statusBillable: '--status-billable',
    statusBillableBg: '--status-billable-bg',
    statusInProcess: '--status-in-process',
    statusDebitedBg: '--status-debited-bg',
    statusAuthorized: '--status-authorized',
    statusAuthorizedBg: '--status-authorized-bg',
    statusExpiringSoon: '--status-expiring-soon',
    statusExpiringSoonBg: '--status-expiring-soon-bg',
    statusExpired: '--status-expired',
    statusExpiredBg: '--status-expired-bg',
    statusUnauthorized: '--status-unauthorized',
    statusUnauthorizedBg: '--status-unauthorized-bg',
    statusProviderActive: '--status-provider-active',
    statusProviderInactive: '--status-provider-inactive',
    statusProviderPossible: '--status-provider-possible',
    statusProviderCancelled: '--status-provider-cancelled',
    statusProviderActiveBg: '--status-provider-active-bg',
    statusProviderInactiveBg: '--status-provider-inactive-bg',
    statusProviderPossibleBg: '--status-provider-possible-bg',
    statusProviderCancelledBg: '--status-provider-cancelled-bg',
    statusSuccess: '--status-success',
    statusSuccessBg: '--status-success-bg',
    statusWarning: '--status-warning',
    statusWarningBg: '--status-warning-bg',
    statusInfo: '--status-info',
    statusInfoBg: '--status-info-bg',
    statusNeutral: '--status-neutral',
    statusNeutralBg: '--status-neutral-bg',
    statusDisabled: '--status-disabled',
    statusDisabledBg: '--status-disabled-bg',
    mapPatientActive: '--map-patient-active',
    mapPatientInactive: '--map-patient-inactive',
    mapProviderActive: '--map-provider-active',
    mapProviderInactive: '--map-provider-inactive',
    mapMarkerProviderActive: '--map-marker-provider-active',
    mapMarkerProviderInactive: '--map-marker-provider-inactive',
    mapMarkerProviderPossible: '--map-marker-provider-possible',
    mapMarkerProviderCancelled: '--map-marker-provider-cancelled',
    mapMarkerPatientActive: '--map-marker-patient-active',
    mapMarkerPatientInactive: '--map-marker-patient-inactive',
    mapMarkerCluster: '--map-marker-cluster',
    white: '--color-white',
    black: '--color-black',
    grey: '--color-grey',
    greyLight: '--color-grey-light',
    greyMedium: '--color-grey-medium',
    greyDark: '--color-grey-dark',
    divider: '--color-divider',
    borderLight: '--color-border-light',
    shadow: '--color-shadow',
};

/** Para estilos inline en TS: `var(--color-primary)` */
export const themeCssVar = (cssVarName: string): string => `var(${cssVarName})`;

export const themeStatusVar = (key: keyof StatusThemeTokens): string =>
    themeCssVar(STATUS_THEME_CSS_VARS[key]);
